import React from 'react';
import { Alert } from '@mantine/core';
import { IconInfoCircle, IconAlertTriangle } from '@tabler/icons-react';
import { IResponseMessage } from '../../../../global/entities/Journal/Journal';

interface ResponseMessageProps {
    messages: IResponseMessage[];
    responseValue?: string;
    subResponseValue?: string;
    currentLang: 'en' | 'fr';
}

export const ResponseMessage: React.FC<ResponseMessageProps> = ({
    messages,
    responseValue,
    subResponseValue,
    currentLang,
}) => {
    if (!messages || messages.length === 0) return null;

    const normalizedResponse = responseValue?.toLowerCase() || '';
    const normalizedSubResponse = subResponseValue?.toLowerCase() || '';

    // Check if a trigger matches a value
    // Supports exact match ("yes", "no") and numeric comparisons (">=6", "<5", ">3", "<=8")
    const matchesTrigger = (trigger: string, value: string): boolean => {
        const numericMatch = trigger.match(/^(>=|<=|>|<)\s*(\d+(\.\d+)?)$/);
        if (numericMatch) {
            const operator = numericMatch[1];
            const threshold = parseFloat(numericMatch[2]);
            const numValue = parseFloat(value);
            if (isNaN(numValue)) return false;
            switch (operator) {
                case '>=': return numValue >= threshold;
                case '<=': return numValue <= threshold;
                case '>':  return numValue > threshold;
                case '<':  return numValue < threshold;
                default:   return false;
            }
        }
        return trigger.toLowerCase() === value.toLowerCase();
    };

    // Parse the operator and threshold from a numeric trigger
    const parseNumericTrigger = (trigger: string): { operator: string; threshold: number } | null => {
        const match = trigger.match(/^(>=|<=|>|<)\s*(\d+(\.\d+)?)$/);
        if (!match) return null;
        return { operator: match[1], threshold: parseFloat(match[2]) };
    };

    // Among matching numeric triggers, keep only the most specific per direction:
    // For > and >= (ascending): highest threshold wins (">8" is more specific than ">=5")
    // For < and <= (descending): lowest threshold wins ("<3" is more specific than "<5")
    const filterMostRelevantNumeric = (matched: IResponseMessage[]): IResponseMessage[] => {
        const exact: IResponseMessage[] = [];
        const ascending: IResponseMessage[] = [];  // > and >=
        const descending: IResponseMessage[] = []; // < and <=

        for (const msg of matched) {
            const parsed = parseNumericTrigger(msg.triggerValue.trim());
            if (!parsed) 
                exact.push(msg);
             else if (parsed.operator === '>' || parsed.operator === '>=') 
                ascending.push(msg);
             else 
                descending.push(msg);
            
        }

        const result: IResponseMessage[] = [...exact];

        if (ascending.length > 0) {
            const best = ascending.reduce((a, b) => {
                const pa = parseNumericTrigger(a.triggerValue.trim())!;
                const pb = parseNumericTrigger(b.triggerValue.trim())!;
                return pb.threshold > pa.threshold ? b : a;
            });
            result.push(best);
        }

        if (descending.length > 0) {
            const best = descending.reduce((a, b) => {
                const pa = parseNumericTrigger(a.triggerValue.trim())!;
                const pb = parseNumericTrigger(b.triggerValue.trim())!;
                return pb.threshold < pa.threshold ? b : a;
            });
            result.push(best);
        }

        return result;
    };

    // Check which messages should be displayed based on current response
    // Match against responseValue or subResponseValue (for compound question types)
    // For multi-select, responseValue can be comma-separated
    const selectedValues = normalizedResponse.split(',').map(v => v.trim());

    const allMatched = messages.filter(msg => {
        const trigger = msg.triggerValue.trim();
        return selectedValues.some(v => matchesTrigger(trigger, v))
            || (normalizedSubResponse && matchesTrigger(trigger, normalizedSubResponse));
    });

    const matchedMessages = filterMostRelevantNumeric(allMatched);

    if (matchedMessages.length === 0) return null;

    return (
        <>
            {matchedMessages.map((msg, index) => {
                const icon = msg.severity === 'warning'
                    ? <IconAlertTriangle size={20} />
                    : <IconInfoCircle size={20} />;
                const color = msg.severity === 'warning' ? 'orange' : 'blue';
                const message = currentLang === 'fr' ? msg.messageFr : msg.messageEn;

                return (
                    <Alert key={index} icon={icon} color={color} variant="light" mt="sm">
                        {message}
                    </Alert>
                );
            })}
        </>
    );
};
